import React, { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { getLocalizedPath } from "@/components/shared/formatURL";
import { hideTags, saveTag } from "@/lib/api/tag";
import { useGlobalContext } from "@/app/context/store";
import { Search } from "lucide-react";

interface ActionTagCardProps {
  tag: any;
  style: string;
  state: any;
  translations: any;
  tagType: string;
}

const ActionTagCard: React.FC<ActionTagCardProps> = ({ tag, style, state, translations, tagType }) => {
  const allMasterPostsURL = getLocalizedPath(`tags/${tag._id}?style=${style}&type=master`); //URL to all posts with all variants of master tag
  const variantsURL = getLocalizedPath(`tag/?id=${tag._id}&style=${style}&browse=true`); //URL to all variants of master tag
 // const searchURL = getLocalizedPath(`post/search?search=${tag.names[translations.key]}&style=${style}`); //URL to search for all posts with master tag name
  
  const { user, fetchUser } = useGlobalContext();

  // hideable if !tag.default and in saved or tag.default and not in hidden
  const isHideable = (tag: any) => {
    return (
      (!tag.default && user.preferences.tags?.saved?.some((item: any) => item.tag === tag.singleVariantId)) ||
      (tag.default && !user.preferences.tags?.hidden?.some((item: any) => item.tag === tag.singleVariantId))
    );
  };

  // saveable if !tag.default && not in saved or tag.default && in hidden
  const isSaveable = (tag: any) => {
    return (
      (!tag.default && !user.preferences.tags?.saved?.some((item: any) => item.tag === tag.singleVariantId)) ||
      (tag.default && user.preferences.tags?.hidden?.some((item: any) => item.tag === tag.singleVariantId))
    );
  };

  const [showAddTag, setShowAddTag] = useState(isSaveable(tag));
  const [showHideTag, setShowHideTag] = useState(isHideable(tag));

  useEffect(() => {
    setShowAddTag(isSaveable(tag));
    setShowHideTag(isHideable(tag));
  }, [user]);

  const addTag = async () => {
    await saveTag(tag.singleVariantId);
    await fetchUser();
  };

  const hideTag = async () => {
    await hideTags([tag.singleVariantId]);
    await fetchUser();
  };

  return (
    <div
      className={`tag-card overflow-hidden ${
        state.expandContent ? "" : "border-[#3C3C3C] sm:rounded-lg sm:border-[0.5px] sm:shadow-lg"
      } `}
    >
      <div className="relative">
        {tag.featuredImageUrl ? (
          <Link prefetch={false} href={allMasterPostsURL}>
            <Image
              src={tag.featuredImageUrl}
              alt={`Tag ${tag._id}`}
              className="h-48 w-full object-cover"
              layout="responsive"
              width={2}
              height={3}
              loading="lazy"
            />
          </Link>
        ) : (
          <Link prefetch={false} href={allMasterPostsURL}>
            <Image
              src={`https://cdn6.imgpog.com/defaultugc.webp`}
              alt={`Tag ${tag._id}`}
              className="h-48 w-full object-cover"
              layout="responsive"
              width={2}
              height={3}
              loading="lazy"
            />
          </Link>
        )}
        <div className="text-md absolute bottom-0 left-0 w-full whitespace-nowrap bg-black bg-opacity-50 py-2 text-center font-semibold text-white sm:text-lg">
          <div className={`w-full text-center font-semibold text-white text-lg pt-2 `}>
            {tag.names[translations.key]}
          </div>
          <div className={`flex justify-center items-center space-x-3 p-3`}>
            {showAddTag && tagType === "variant" && (
              <>
                {/* Add Tag button */}
                <button
                  onClick={addTag}
                  className={`border-grey-outline hover:bg-crp-red bg-crp-dark-grey border-[0.5px] rounded-lg w-fit items-center py-2 px-4 text-center text-sm font-semibold text-white  ${
                    tag.default !== true ? "border-[0.5px] border-ugc-tags" : ""
                  }`}
                >
                  + {translations.addTag}
                </button>
              </>
            )}
            {tagType === "master" && (
                <>
                {/* See variants button */}
                <Link
                  href={variantsURL}
                  className={`border-grey-outline hover:bg-crp-red bg-crp-dark-grey border-[0.5px] rounded-lg w-fit items-center py-2 px-4 text-center text-sm font-semibold text-white`}
                >
                  {translations.seeVariants}
                </Link>
              </>
            )}
            {showHideTag && tagType === "variant" && (
              <>
                {/* Hide Tag button */}
                <button
                  onClick={hideTag}
                  className={`border-grey-outline hover:bg-crp-red bg-crp-dark-grey border-[0.5px] rounded-lg w-fit items-center py-2 px-4 text-center text-sm font-semibold text-white ${
                    tag.default !== true ? "border-[0.5px] border-ugc-tags" : ""
                  }`}
                  >
                  {translations.hideTag}
                </button>
              </>
            )}

            <Link className="rounded-lg border-grey-outline border-[0.5px] p-2 hover:bg-crp-red bg-crp-dark-grey" href={allMasterPostsURL}>
              <Search className="my-auto text-white" size={19.5} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActionTagCard;
